import type {
  Locale,
  SkillCompleteResult,
  SkillContestDetail,
  SkillContestList,
  SkillCreateInput,
  SkillEnterResult,
  SkillGameId,
  SkillImportInput,
  SkillImportResult,
  SkillSaveInput,
  SkillSaveResult,
  SkillSessionList,
  SkillSessionView,
} from '@tg/shared';
import { api, newIdempotencyKey } from '@/api/client';

/** REST of the skill games (@tg/shared skill.ts; server apps/server/src/skill/routes.ts). Signed-in players only. */
export const skillApi = {
  create: (b: Omit<SkillCreateInput, 'idempotencyKey'> & { idempotencyKey?: string }) =>
    api.post<SkillSessionView>('/api/skill/sessions', { ...b, idempotencyKey: b.idempotencyKey ?? newIdempotencyKey() }),
  import: (b: SkillImportInput) => api.post<SkillImportResult>('/api/skill/sessions/import', b),
  list: (q: { game?: SkillGameId; status?: 'active' | 'finished'; limit?: number } = {}) => {
    const p = new URLSearchParams();
    if (q.game) p.set('game', q.game);
    if (q.status) p.set('status', q.status);
    if (q.limit) p.set('limit', String(q.limit));
    const s = p.toString();
    return api.get<SkillSessionList>(`/api/skill/sessions${s ? `?${s}` : ''}`);
  },
  get: (id: string) => api.get<SkillSessionView>(`/api/skill/sessions/${encodeURIComponent(id)}`),
  save: (id: string, b: SkillSaveInput) => api.put<SkillSaveResult>(`/api/skill/sessions/${encodeURIComponent(id)}`, b),
  complete: (id: string, b: SkillSaveInput) => api.post<SkillCompleteResult>(`/api/skill/sessions/${encodeURIComponent(id)}/complete`, b),
  abandon: (id: string, version: number) => api.post<SkillSaveResult>(`/api/skill/sessions/${encodeURIComponent(id)}/abandon`, { version }),
  /** `locale`: the language of a per-language contest (daily-words); the server defaults to en */
  contests: (game: SkillGameId, locale?: Locale) =>
    api.get<SkillContestList>(`/api/skill/contests?game=${encodeURIComponent(game)}${locale ? `&locale=${locale}` : ''}`),
  contest: (id: string) => api.get<SkillContestDetail>(`/api/skill/contests/${encodeURIComponent(id)}`),
  enter: (contestId: string, idempotencyKey = newIdempotencyKey()) =>
    api.post<SkillEnterResult>(`/api/skill/contests/${encodeURIComponent(contestId)}/enter`, { idempotencyKey }),
};
